import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import PapersMenu from 'arts/components/PapersMenu/papers_menu';

// Convert wall builder structure to art page structure
const convertOptionsToArtPageFormat = (workSizes) => {
  if (!workSizes || !workSizes.length) return [];

  return workSizes.map(ws => ({
    paperId: ws.id,  // Map workSizeId to paperId
    name: ws.name,
    width: ws.width,
    height: ws.height,
    options: ws.options || [],
    price: ws.price
  }));
};

// Create selection object from wall builder props
const createSelectionObject = (workSizeId, frameId, workSizes, nonGlare) => {
  if (!workSizes || !workSizeId) return { paper: null, frame: null, nonGlare };

  const workSize = workSizes.find(ws => ws.id === workSizeId);
  if (!workSize) return { paper: null, frame: null, nonGlare };

  const paper = {
    paperId: workSize.id,
    name: workSize.name,
    width: workSize.width,
    height: workSize.height,
    price: workSize.price
  };

  let frame = null;
  if (frameId) {
    frame = (workSize.options || []).find(opt => opt.frameId === frameId) || null;
  } else if (workSize.options && workSize.options.length) {
    // For no frame, use first option (which should be no frame option)
    // eslint-disable-next-line prefer-destructuring
    frame = workSize.options[0];
  }

  return { paper, frame, nonGlare };
};

const PapersMenuAdapter = ({
  workSizes,
  workSizeId,
  frameId,
  nonGlare,
  setFrame,
  setWorkSize,
  setNonGlare,
  thumbnails,
  currency,
  landscape,
  initialPaperId,
  initialFrameId,
  showNoFrameOption
}) => {
  // Convert wall builder data to art page format
  const data = useMemo(
    () => convertOptionsToArtPageFormat(workSizes),
    [workSizes]
  );

  // Map wall builder state to art page selection state
  const selection = useMemo(
    () => createSelectionObject(workSizeId, frameId, workSizes, nonGlare),
    [workSizeId, frameId, workSizes, nonGlare]
  );

  // Current price calculation from art page
  const currentPrice = useMemo(() => {
    if (!selection.frame || !selection.paper) return 0;

    const basePrice = selection.paper.price || 0;
    const framePrice = selection.frame.framePrice || 0;
    const nonGlarePrice = (nonGlare && selection.frame.nonGlarePrice)
      ? selection.frame.nonGlarePrice
      : 0;

    return basePrice + framePrice + nonGlarePrice;
  }, [selection, nonGlare]);

  // Map art page actions to wall builder actions
  const handleSetFrame = (paper, frame) => {
    if (!paper || !frame) return;

    // In wall builder, we use workSizeId and frameId
    if (paper.paperId !== workSizeId) {
      setWorkSize(paper.paperId);
    }

    setFrame(frame.frameId);
  };

  const handleSetNonGlare = (value) => {
    setNonGlare(value);
  };

  const handleResetSelection = () => {
    // No direct equivalent in wall builder, but we can reset frame
    setFrame(null);
    setNonGlare(false);
  };

  return (
    <PapersMenu
      data={ data }
      setFrame={ handleSetFrame }
      setNonGlare={ handleSetNonGlare }
      selection={ selection }
      resetSelection={ handleResetSelection }
      initialPaperId={ initialPaperId || workSizeId }
      initialFrameId={ initialFrameId || frameId }
      currentPrice={ currentPrice }
      currency={ currency }
      landscape={ landscape }
      nonGlare={ nonGlare }
      thumbnails={ thumbnails }
      showNoFrameOption={ showNoFrameOption }
      wallBuilderMode
    />
  );
};

PapersMenuAdapter.propTypes = {
  workSizes: PropTypes.array,
  workSizeId: PropTypes.number,
  frameId: PropTypes.number,
  nonGlare: PropTypes.bool,
  setFrame: PropTypes.func.isRequired,
  setWorkSize: PropTypes.func.isRequired,
  setNonGlare: PropTypes.func.isRequired,
  thumbnails: PropTypes.array,
  currency: PropTypes.string,
  landscape: PropTypes.bool,
  initialPaperId: PropTypes.number,
  initialFrameId: PropTypes.number,
  showNoFrameOption: PropTypes.bool
};

PapersMenuAdapter.defaultProps = {
  nonGlare: false,
  landscape: false,
  showNoFrameOption: true,
  workSizes: [],
  thumbnails: []
};

export default PapersMenuAdapter;
